import {nowMs} from '../data/clock'

/**
 * Pure aggregation for the SKU Weight Intelligence and Recovery Analytics
 * tabs (Task 9). Nothing here touches React — SkuIntelligence.js and
 * RecoveryAnalytics.js only call these functions and render the result,
 * mirroring actions.js / EvidenceStudio.js's split between pure logic and
 * presentation so the maths can be unit-tested without mounting anything.
 *
 * Every function takes the caller's already range-filtered `rows` (see
 * index.js's `overviewRows`, which both the KPI strip and these two tabs
 * are fed from) — none of them re-derive or widen the row set, which is
 * exactly what keeps their numbers reconciled with the KPI strip above.
 */

const WEEK_MS = 7 * 86400000

/**
 * Rows counted as "recovered" — kept byte-for-byte identical to the `won`
 * filter in state/query.js's queryTotals (see query.js, the lines defining
 * `won` and `recovered`) so this module's Recovered figures can never drift
 * from the KPI strip's. Duplicated rather than imported because queryTotals
 * inlines the check instead of exporting it; change both together if that
 * ever changes.
 */
function isRecovered(row) {
  return row.status === 'ACCEPTED_BY_COURIER' || row.status === 'ACCEPTED_PROSHIP'
}

/**
 * Which 7-day bucket, counting back from the pinned NOW, `dateInput` falls
 * into. Bucket 0 covers the 7 days immediately before NOW, bucket 1 the 7
 * days before that, and so on — deterministic against NOW alone, never the
 * wall clock or calendar week boundaries, so the same rows always bucket
 * the same way. A date at or after NOW (not expected in this frozen
 * dataset, but not impossible in a fixture) clamps to bucket 0 rather than
 * going negative.
 */
export function weekIndexOf(dateInput) {
  const diffMs = nowMs() - new Date(dateInput).getTime()
  return Math.max(0, Math.floor(diffMs / WEEK_MS))
}

function weekLabel(index) {
  const end = new Date(nowMs() - index * WEEK_MS)
  const start = new Date(nowMs() - (index + 1) * WEEK_MS)
  const shortDate = (d) => d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})
  return `${shortDate(start)} – ${shortDate(new Date(end.getTime() - 86400000))}`
}

/**
 * Raised / recovered / cases per 7-day bucket (see weekIndexOf), oldest
 * bucket first. Only buckets that contain at least one row are returned —
 * this dataset spans a handful of weeks, so padding in empty buckets would
 * add rows with nothing to show.
 *
 * "Raised" is total indicative exposure (sum of excessAmount) for rows
 * created in that week. "Recovered" is the same sum restricted to rows
 * isRecovered() accepts. "Share" is recovered/raised for that week as a
 * whole-number percentage (0 when the week raised nothing). "Cases" is the
 * shipment count for the week; "recoveredCases" is that same count
 * restricted to isRecovered() rows — the count-series counterpart to
 * raised/recovered, incremented inside the same branch so it can never
 * disagree with which rows contributed to "recovered".
 *
 * Every row in `rows` lands in exactly one bucket by createdAt, so summing
 * `cases` across the returned array always equals rows.length — no row is
 * ever dropped or counted twice.
 */
export function weeklyBreakdown(rows) {
  const list = rows || []
  const buckets = new Map()

  list.forEach((row) => {
    const index = weekIndexOf(row.createdAt)
    let bucket = buckets.get(index)
    if (!bucket) {
      bucket = {weekIndex: index, label: weekLabel(index), raised: 0, recovered: 0, cases: 0, recoveredCases: 0}
      buckets.set(index, bucket)
    }
    bucket.raised += row.excessAmount
    bucket.cases += 1
    if (isRecovered(row)) {
      bucket.recovered += row.excessAmount
      bucket.recoveredCases += 1
    }
  })

  return Array.from(buckets.values())
    .sort((a, b) => b.weekIndex - a.weekIndex) // largest index = furthest in the past = oldest first
    .map((b) => ({...b, share: b.raised > 0 ? Math.round((b.recovered / b.raised) * 100) : 0}))
}

/**
 * Discrepancy count and total indicative exposure per courier, sorted by
 * exposure descending. Every courier that appears in `rows` appears here —
 * nothing is pre-seeded from data/reference.js's COURIERS list, so a
 * courier with zero rows in the current range/filter simply doesn't show.
 */
export function courierBreakdown(rows) {
  const list = rows || []
  const byCourier = new Map()

  list.forEach((row) => {
    let c = byCourier.get(row.courier)
    if (!c) {
      c = {courier: row.courier, discrepancies: 0, exposure: 0}
      byCourier.set(row.courier, c)
    }
    c.discrepancies += 1
    c.exposure += row.excessAmount
  })

  return Array.from(byCourier.values()).sort((a, b) => b.exposure - a.exposure)
}

/**
 * Per-SKU roll-up, sorted by exposure descending. `openCount` and
 * `exposure` count every dispute ever raised for the SKU within `rows`
 * (not only its currently-unresolved ones) — this mirrors how
 * data/noberoDisputes.js's precomputed SKUS export was generated; see
 * analytics.test.js's reconciliation against SKUS for the full, unfiltered
 * dataset. `weights` is the histogram of appliedWeight values seen for the
 * SKU, most frequent first, tied broken by weight ascending.
 *
 * `suggestedWeight` (Phase D) is that same histogram's own mode —
 * `weights[0].weight` — the charged weight couriers actually applied most
 * often for this SKU. It is real, observed data, never invented: it is a
 * plain re-read of `weights`, not a separate estimate, so the two can never
 * disagree. `dims`/`deadKg`/`volKg` are copied from one real row that
 * carries `suggestedWeight` as its own `appliedWeight` — the same
 * Phase-A-back-solved dead/volumetric split that row has everywhere else —
 * so SkuIntelligence.js can run this SKU's figures through
 * state/query.js's governingType() exactly the way DisputeGrid.js and
 * RecordSections.js run a single row's, and never disagree with them about
 * which figure governs.
 *
 * `declaredVolKg`/`declaredDims` are the DECLARED side's counterpart and are
 * deliberately NOT taken from that same row. They come from the row
 * `modalWeight` came from, so the merchant figure and the carton it was derived
 * from always describe one shipment. The two triples answer different
 * questions — what the merchant claimed, versus what the courier billed — and
 * sourcing both from the billed row would silently pair a weight with someone
 * else's box. The declared carton is fabricated by construction (see
 * data/noberoDisputes.js's provenance header) and its volumetric always lands
 * below `originalWeight`, which is why no governing side is derived for it:
 * dead would win every row.
 *
 * Grouped by `sku || product` rather than `sku` alone: four real rows in
 * the frozen dataset share an empty sku string but a common product name
 * (a genuine gap in the source data, not a bug), and grouping by sku alone
 * would incorrectly merge them with any other empty-sku SKU.
 */
/**
 * FABRICATED — a lifetime shipment count for a SKU.
 *
 * This module's dataset only knows about DISPUTED shipments; nothing in it
 * records how many times a SKU shipped in total, and the column asking for
 * that number was requested anyway. So it is invented — but invented
 * deterministically, from a hash of the SKU code, because every other number
 * on this page is a pure function of the data and a count that changed between
 * renders would be worse than an honest placeholder.
 *
 * Always a multiple of the open-dispute count and never below it, so the two
 * columns cannot contradict each other on screen (8 open of 3 shipped).
 *
 * Replace this the moment a real shipment total is available — nothing else
 * reads it, so it is a one-line swap.
 */
function fabricatedShipments(key, openCount) {
  const k = String(key || '')
  let h = 0
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) % 100000
  return Math.max(openCount, openCount * (6 + (h % 26)))
}

/**
 * `shipmentCounts` — optional {sku: lifetimeShipments} from the weight ledger
 * (data/api.js apiSkuShipmentCounts). When a SKU has a real figure it is used
 * (floored at the open-dispute count so the two columns still can't contradict
 * each other); anything without one keeps the deterministic placeholder, which
 * is also what every seeded/test mount gets.
 */
export function skuBreakdown(rows, shipmentCounts) {
  const list = rows || []
  const bySku = new Map()
  const counts = shipmentCounts || null

  list.forEach((row) => {
    const key = row.sku || row.product
    let s = bySku.get(key)
    if (!s) {
      s = {
        sku: row.sku,
        product: row.product,
        modalWeight: row.originalWeight,
        // The declared carton behind modalWeight, captured from the SAME row in
        // the SAME statement — not from `sampleRow` below. sampleRow is chosen
        // for its APPLIED weight, so its carton belongs to the billed side; if
        // the declared breakdown were sourced from it, the box on screen would
        // belong to a different shipment than the weight beside it and nothing
        // would ever flag the mismatch. No declaredDeadKg: originalWeight IS
        // the declared dead weight, so modalWeight already is that figure and a
        // second copy could only drift from it.
        declaredVolKg: row.declaredVolKg,
        declaredDims: row.declaredDims,
        openCount: 0,
        exposure: 0,
        weights: new Map(),
        // First-seen row for each distinct appliedWeight — kept only to
        // source dims/deadKg/volKg for whichever weight turns out to be the
        // mode (below), never exposed itself.
        rowByWeight: new Map(),
      }
      bySku.set(key, s)
    }
    s.openCount += 1
    s.exposure += row.excessAmount
    s.weights.set(row.appliedWeight, (s.weights.get(row.appliedWeight) || 0) + 1)
    if (!s.rowByWeight.has(row.appliedWeight)) s.rowByWeight.set(row.appliedWeight, row)
  })

  return Array.from(bySku.values())
    .map((s) => {
      const weights = Array.from(s.weights.entries())
        .map(([weight, count]) => ({weight, count}))
        .sort((a, b) => b.count - a.count || a.weight - b.weight)
      const suggestedWeight = weights.length ? weights[0].weight : s.modalWeight
      const sampleRow = s.rowByWeight.get(suggestedWeight)
      const offModal = weights.reduce((n, w) => n + (w.weight === suggestedWeight ? 0 : w.count), 0)

      return {
        sku: s.sku,
        product: s.product,
        offModal,
        shipments: counts && s.sku && Number.isFinite(counts[s.sku])
          ? Math.max(counts[s.sku], s.openCount)
          : fabricatedShipments(s.sku || s.product, s.openCount),
        modalWeight: s.modalWeight,
        declaredVolKg: s.declaredVolKg,
        declaredDims: s.declaredDims,
        openCount: s.openCount,
        exposure: s.exposure,
        weights,
        suggestedWeight,
        dims: sampleRow ? sampleRow.dims : undefined,
        deadKg: sampleRow ? sampleRow.deadKg : undefined,
        volKg: sampleRow ? sampleRow.volKg : undefined,
      }
    })
    .sort((a, b) => b.exposure - a.exposure)
}
